/**
 * 父应用通信工具
 * 用于在iframe环境下与msLogin父应用进行通信
 */

export interface ParentMessage {
  type: "AUTH_FAILED" | "TOKEN_UPDATE" | "NAVIGATE" | "REFRESH_TOKEN";
  data?: any;
}

export class ParentCommunication {
  constructor() {
    this.setupMessageListener();
  }

  /**
   * 设置消息监听器
   */
  private setupMessageListener(): void {
    window.addEventListener("message", this.handleMessage.bind(this));
  }

  /**
   * 处理来自父应用的消息
   */
  private handleMessage(event: MessageEvent): void {
    // 安全检查：确保消息来源可信
    if (
      event.origin !== window.location.origin &&
      !event.origin.includes("localhost") &&
      !event.origin.includes("ihoms.cn")
    ) {
      return;
    }

    const { type, data } = event.data;

    switch (type) {
      case "TOKEN_UPDATE":
        this.handleTokenUpdate(data);
        break;
      case "NAVIGATE":
        this.handleNavigation(data);
        break;
      case "REFRESH_TOKEN":
        this.handleTokenRefresh();
        break;
      default:
        // 忽略未知消息类型
        break;
    }
  }

  /**
   * 处理Token更新
   */
  private handleTokenUpdate(data: any): void {
    if (data?.token) {
      localStorage.setItem("mslogin_token", data.token);
      // 触发认证状态更新
      window.dispatchEvent(new CustomEvent("tokenUpdated", { detail: data }));
    }
  }

  /**
   * 处理导航请求
   */
  private handleNavigation(data: any): void {
    if (data?.path && window.history) {
      window.history.pushState(null, "", data.path);
    }
  }

  /**
   * 处理Token刷新
   */
  private handleTokenRefresh(): void {
    // 通知父应用刷新Token
    this.notifyParent({
      type: "REFRESH_TOKEN",
      data: { timestamp: Date.now() },
    });
  }

  /**
   * 通知父应用认证失败
   */
  public notifyAuthFailed(): void {
    this.notifyParent({
      type: "AUTH_FAILED",
      data: { service: "msadmin" },
    });
  }

  /**
   * 通知父应用Token更新
   */
  public notifyTokenUpdate(token: string): void {
    this.notifyParent({
      type: "TOKEN_UPDATE",
      data: { token },
    });
  }

  /**
   * 通知父应用导航请求
   */
  public notifyNavigation(path: string): void {
    this.notifyParent({
      type: "NAVIGATE",
      data: { path },
    });
  }

  /**
   * 向父应用发送消息
   */
  private notifyParent(message: ParentMessage): void {
    if (window.parent !== window) {
      window.parent.postMessage(message, "*");
    }
  }

  /**
   * 检查是否在iframe环境中
   */
  public isInIframe(): boolean {
    return window.parent !== window;
  }

  /**
   * 获取父应用信息
   */
  public getParentInfo(): { isIframe: boolean; parentOrigin?: string } {
    return {
      isIframe: this.isInIframe(),
      parentOrigin: this.isInIframe() ? document.referrer : undefined,
    };
  }
}

// 创建全局实例
export const parentComm = new ParentCommunication();

// 导出便捷方法
export const notifyAuthFailed = () => parentComm.notifyAuthFailed();
export const notifyTokenUpdate = (token: string) => parentComm.notifyTokenUpdate(token);
export const notifyNavigation = (path: string) => parentComm.notifyNavigation(path);
export const isInIframe = () => parentComm.isInIframe();

export default parentComm;
